/**
 * `/cli/authorize` — landing page for `clickweave login` (P4-b/5).
 *
 * The CLI generates a PKCE pair, then opens this page with the
 * `code_challenge` (+ optional `redirect_uri`, `state`, `scopes`) in
 * the query string. We show a consent panel naming the active org +
 * the requested scopes, then on click:
 *
 *   1. POST /v1/cli/sessions with the JWT and the code_challenge.
 *   2. Receive a `code`.
 *   3. Either redirect the browser to the CLI's localhost callback
 *      (`?code=<>&state=<>`) — the browser flow — or render the
 *      code on-screen for the operator to paste back into the CLI
 *      — the manual fallback when `redirect_uri` is absent.
 *
 * Security:
 *   - `redirect_uri`, when present, must be an `http://127.0.0.1:<port>`
 *     or `http://localhost:<port>` URL. Anything else is rejected
 *     before the network call — prevents open-redirect abuse.
 *   - `state` round-trips verbatim. The CLI checks it on the
 *     callback for CSRF safety.
 *   - This page sits under `_app/`, so Clerk auth is enforced by
 *     AuthGate before this code runs. Unauthenticated users get
 *     bounced to `/sign-in?redirect=<this URL>` automatically.
 */
import { ErrorState } from '@/components/primitives/ErrorState';
import { SecretReveal } from '@/components/primitives/SecretReveal';
import { Island } from '@/components/shell/Island';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { apiFetch } from '@/lib/api';
import { toastFromError } from '@/lib/errors';
import { useCliSessions } from '@/lib/queries';
import { useOrganization } from '@clerk/react';
import { Terminal, Warning } from '@phosphor-icons/react';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

const AuthorizeSearch = z.object({
  code_challenge: z.string().min(43).max(128).optional(),
  code_challenge_method: z.literal('S256').optional(),
  scopes: z.string().optional(),
  redirect_uri: z.string().optional(),
  state: z.string().optional(),
  hostname: z.string().max(128).optional(),
});

const DEFAULT_SCOPES = ['member'] as const;

export const Route = createFileRoute('/_app/cli/authorize')({
  validateSearch: (s) => AuthorizeSearch.parse(s),
  component: CliAuthorizePage,
});

interface SessionResponse {
  code: string;
  expires_at: string;
}

function CliAuthorizePage(): ReactElement {
  const search = useSearch({ from: '/_app/cli/authorize' });
  // Org name comes straight from Clerk's session (no API round-trip)
  // — same data path the sidebar uses, which is why the sidebar
  // renders the org name long before /v1/workspace would resolve.
  const { organization: activeOrg, isLoaded: orgLoaded } = useOrganization();
  // CLI sessions only — the operator's user-source keys are
  // irrelevant for the trust-device check. Hitting `?source=cli`
  // keeps the response small and avoids the dashboard's main keys
  // page invalidating on every cli/authorize visit.
  const keys = useCliSessions();
  const [issuedCode, setIssuedCode] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoFiredRef = useRef(false);

  const codeChallenge = search.code_challenge;
  const redirectUri = search.redirect_uri;
  const state = search.state ?? '';
  const requestedHostname = search.hostname;
  // Scopes are optional in the URL. When absent, the dashboard hands
  // an empty array to the API; the API picks role-based defaults
  // (admin role → ['admin'], member → ['member']). The CLI omits
  // `&scopes=` for the common case so the user doesn't see scope
  // jargon at all unless they explicitly downscoped via `--scopes`.
  const requestedScopes = search.scopes
    ? search.scopes
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  // Validate redirect_uri up front — never network with a bad one.
  const redirectError = redirectUri ? validateRedirect(redirectUri) : null;

  // Trusted-device shortcut: if the CLI passed `?hostname=…` and we
  // can find an active (non-revoked) key in this org whose name
  // matches `clickweave-cli on <hostname>`, treat that as a prior
  // consent from this device. The user already approved the CLI on
  // this hostname; another login is just minting a fresh key for the
  // same trust relationship. New hostnames (e.g., a second laptop)
  // still hit the explicit consent UI.
  const expectedKeyName = requestedHostname ? `clickweave-cli on ${requestedHostname}` : null;
  const trustedDevice = Boolean(
    expectedKeyName && keys.data?.some((k) => k.name === expectedKeyName && k.revoked_at == null),
  );

  // Hold a neutral spinner until we know which UI to render. Without
  // this, `trustedDevice` starts false (keys not loaded yet) and the
  // header would briefly render "Confirm CLI access" before flipping
  // to "Welcome back" once keys lands — an ugly flicker.
  const decisionReady = !keys.isLoading;

  // Auto-fire handleAuthorize the moment we know this is a trusted
  // device. Guarded by autoFiredRef so React 19 transitions / strict-
  // mode double-mounts can't pump the network call twice.
  //
  // Note we intentionally do NOT wait for `org` — the server side of
  // POST /v1/cli/sessions resolves the org from the JWT claim, and a
  // hostname-matching active key already proves the user is in this
  // org (useKeys is JWT-scoped). Waiting on useWorkspace was the
  // dominant perceived delay (200–500 ms) before this trim.
  //
  // We still require `redirectUri` so the manual-mode fallback (no
  // localhost listener — the operator must paste the code into the
  // CLI) keeps its explicit click. Without that, an auto-fired code
  // would land on a screen the user didn't expect.
  useEffect(() => {
    if (autoFiredRef.current) return;
    if (!trustedDevice) return;
    if (!codeChallenge) return;
    if (!redirectUri) return;
    if (redirectError) return;
    if (issuedCode) return;
    if (error) return;
    if (submitting) return;
    autoFiredRef.current = true;
    void handleAuthorize();
    // handleAuthorize is intentionally not in deps — autoFiredRef
    // guarantees single-fire, and including it would create a new
    // identity every render and re-run on every state change.
  }, [trustedDevice, codeChallenge, redirectUri, redirectError, issuedCode, error, submitting]);

  async function handleAuthorize() {
    if (!codeChallenge) return;
    if (redirectUri && redirectError) {
      setError(redirectError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Only forward `scopes` if the CLI explicitly asked for some.
      // Empty/missing maps to "API decides from role" — the handler
      // picks `['admin']` for admins, `['member']` for members.
      const body = await apiFetch<SessionResponse>('/v1/cli/sessions', {
        method: 'POST',
        body: {
          code_challenge: codeChallenge,
          code_challenge_method: 'S256',
          ...(requestedScopes.length > 0 ? { scopes: requestedScopes } : {}),
        },
      });
      if (redirectUri) {
        const url = new URL(redirectUri);
        url.searchParams.set('code', body.code);
        if (state) url.searchParams.set('state', state);
        window.location.assign(url.toString());
        return;
      }
      setIssuedCode(body.code);
    } catch (err) {
      toastFromError(err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  // Missing-PKCE is the only failure that should override the
  // decision-pending wait — there's nothing for keys.data to tell us
  // either way, so show the error immediately.
  if (!codeChallenge) {
    return (
      <Island title="Authorize Clickweave CLI">
        <div className="mx-auto max-w-xl space-y-6 p-6">
          <Header subtitle="The Clickweave CLI is requesting an API key bound to your account." />
          <Alert variant="error">
            <Warning aria-hidden="true" weight="light" />
            <AlertTitle>Missing PKCE challenge</AlertTitle>
            <AlertDescription>
              Open this page from <code className="font-mono">clickweave login</code>, not directly.
            </AlertDescription>
          </Alert>
        </div>
      </Island>
    );
  }

  // Neutral spinner while we work out whether this is a trusted-device
  // auto-skip or a fresh consent flow. No header text yet — that's the
  // mid-paint flicker we're avoiding.
  if (!decisionReady) {
    return (
      <Island title="Authorize Clickweave CLI">
        <div className="mx-auto flex max-w-xl flex-col items-center gap-3 p-12 text-center">
          <Spinner className="size-5 text-[var(--color-mute)]" />
          <p className="text-sm text-[var(--color-mute)]">Checking your devices…</p>
        </div>
      </Island>
    );
  }

  // Trusted-device path: no header — the page is on screen for ~one
  // network hop before the localhost redirect fires, so a heading
  // would just flash and disappear. Just the spinner that continues
  // the visual from the `!decisionReady` state.
  if (trustedDevice) {
    return (
      <Island title="Authorize Clickweave CLI">
        <div className="mx-auto flex max-w-xl flex-col items-center gap-3 p-12 text-center">
          <Spinner className="size-5 text-[var(--color-mute)]" />
          <p className="text-sm text-[var(--color-mute)]">
            {error ? error : 'Issuing a fresh credential…'}
          </p>
          {error && !redirectError && (
            <ErrorState
              title="Authorization failed"
              error={new Error(error)}
              retry={() => {
                setError(null);
                void handleAuthorize();
              }}
            />
          )}
        </div>
      </Island>
    );
  }

  // Consent path (new device / first login). Org name from Clerk —
  // instant render, no skeleton.
  return (
    <Island title="Authorize Clickweave CLI">
      <div className="mx-auto max-w-xl space-y-6 p-6">
        <Header
          heading="Confirm CLI access"
          subtitle="The Clickweave CLI is requesting an API key bound to your account. Review the details below before authorizing."
        />
        <section className="overflow-hidden rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
          <dl className="divide-y divide-[var(--color-line-soft)] text-sm">
            <Row label="Organisation">
              {!orgLoaded ? (
                <span className="text-[var(--color-mute)]">…</span>
              ) : activeOrg ? (
                <span className="font-medium text-[var(--color-ink)]">{activeOrg.name}</span>
              ) : (
                <span className="text-[var(--color-mute)]">None — switch into an org first</span>
              )}
            </Row>
            <Row label="Access">
              {requestedScopes.length > 0 ? (
                // Custom scope set — show what was requested. Only
                // happens when the operator passed `--scopes` to the
                // CLI (a CI / read-only key), which is rare.
                <div className="flex flex-wrap gap-1.5">
                  {requestedScopes.map((s) => (
                    <span
                      key={s}
                      className="rounded-md border border-[var(--color-line-soft)] bg-[var(--color-paper-alt)] px-1.5 py-0.5 font-mono text-xs text-[var(--color-ink)]"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-[var(--color-mute)]">
                  The CLI will act with the same permissions as you in this organisation.
                </span>
              )}
            </Row>
            <Row label="Redirect">
              {redirectUri ? (
                <span className="break-all font-mono text-xs text-[var(--color-ink)]">
                  {redirectUri}
                </span>
              ) : (
                <span className="text-[var(--color-mute)]">
                  Manual — code will be shown to copy
                </span>
              )}
            </Row>
          </dl>
        </section>

        {redirectError && (
          <Alert variant="error">
            <Warning aria-hidden="true" weight="light" />
            <AlertTitle>Redirect rejected</AlertTitle>
            <AlertDescription>{redirectError}</AlertDescription>
          </Alert>
        )}

        {error && !redirectError && (
          <ErrorState
            title="Authorization failed"
            error={new Error(error)}
            retry={() => {
              setError(null);
              void handleAuthorize();
            }}
          />
        )}

        {issuedCode ? (
          <Alert variant="warning">
            <Warning aria-hidden="true" weight="light" />
            <AlertTitle>Copy this code into your CLI</AlertTitle>
            <AlertDescription className="space-y-3">
              <SecretReveal value={issuedCode} defaultMasked={false} />
              <p className="text-xs text-[var(--color-mute)]">
                Expires in 5 minutes. Single-use — paste it once and you're done.
              </p>
            </AlertDescription>
          </Alert>
        ) : (
          <div className="flex justify-end">
            <Button
              onClick={handleAuthorize}
              loading={submitting}
              disabled={!!redirectError || !activeOrg}
            >
              Authorize CLI
            </Button>
          </div>
        )}
      </div>
    </Island>
  );
}

function Header({
  heading = 'Authorize CLI',
  subtitle,
}: {
  heading?: string;
  subtitle: ReactElement | string;
}): ReactElement {
  return (
    <header className="space-y-1">
      <div className="flex items-center gap-2 text-[var(--color-mute)]">
        <Terminal aria-hidden="true" weight="light" />
        <span className="text-xs font-medium uppercase tracking-[0.06em]">CLI sign-in</span>
      </div>
      <h2 className="text-base font-semibold text-[var(--color-ink)]">{heading}</h2>
      <p className="text-sm text-[var(--color-mute)]">{subtitle}</p>
    </header>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: ReactElement | string;
}): ReactElement {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-4 px-4 py-3">
      <dt className="text-xs uppercase tracking-[0.06em] text-[var(--color-mute)]">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

/**
 * Allow only loopback redirects — `http://127.0.0.1:<port>/...` or
 * `http://localhost:<port>/...`. Rejects everything else (https hosts,
 * file://, exotic schemes) so a malicious referrer can't open this
 * page with a redirect_uri pointing at their own server.
 */
function validateRedirect(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return 'not a valid URL';
  }
  if (parsed.protocol !== 'http:') return 'must use http:// (loopback only)';
  if (parsed.hostname !== '127.0.0.1' && parsed.hostname !== 'localhost') {
    return 'host must be 127.0.0.1 or localhost';
  }
  return null;
}
